import { isPlainObject } from '@reduxjs/toolkit';
import type { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { DocumentNode } from 'graphql';
import { ClientError, GraphQLClient, RequestDocument, RequestOptions } from 'graphql-request';
import { RefreshResponse } from 'models/api/auth/refresh-response.model';
import { refreshAccessToken } from 'modules/auth';
import { store } from 'store/store';
import { getAcceptLanguage } from 'utils/locale';

/**
 * Кусок состояния, который нужен baseQuery. Описан локально осознанно: импорт `RootState`
 * из `store/store` замыкает круг по типам (store → rootReducer → api → baseQuery → store),
 * и TypeScript перестаёт выводить тип middleware в `configureStore`.
 */
type AuthState = {
  auth?: { authData?: { access_token?: string; refresh_token?: string } };
};

/**
 * graphql-request принимает строку или DocumentNode. graphql-codegen отдаёт документ как
 * `TypedDocumentString` — объект, у которого нет `definitions`, и `resolveRequestDocument`
 * падает на `extractOperationName`. Приводим такой документ к строке.
 */
function normalizeDocument(document: string | DocumentNode | { toString(): string }) {
  if (typeof document === 'string') {
    return document;
  }
  if ((document as DocumentNode)?.kind === 'Document') {
    return document as DocumentNode;
  }
  return String(document);
}

function applyRequestHeaders(headers: Headers, getState: () => unknown) {
  const token = (getState() as AuthState).auth?.authData?.access_token;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  headers.set('Accept-Language', getAcceptLanguage());
  return headers;
}

export const graphqlRequestBaseQuery = <E = ErrorResponse>(
  options: GraphqlRequestBaseQueryArgs<E>,
): BaseQueryFn<
  { document: string | DocumentNode; variables?: any },
  unknown,
  E,
  Partial<Pick<ClientError, 'request' | 'response'>>
> => {
  const client =
    'client' in options
      ? options.client
      : new GraphQLClient(process.env.REACT_APP_API_BASE_PATH + '/graphql', {
          fetch: (input, init) => window.fetch(input as RequestInfo, init),
        });
  const requestHeaders: RequestHeaders =
    'requestHeaders' in options ? options.requestHeaders : {};

  return async (
    { document, variables },
    { getState, endpoint, forced, type, signal, extra },
  ) => {
    const prepareHeaders: PrepareHeaders = options.prepareHeaders ?? ((x) => x);
    const customErrors = options.customErrors ?? ((error: ClientError) => {
      const { name, message, stack } = error;
      return { name, message, stack };
    });

    const buildHeaders = async (accessToken?: string) => {
      const headers = applyRequestHeaders(
        new Headers(stripUndefined(requestHeaders)),
        getState,
      );
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      return prepareHeaders(headers, {
        getState,
        endpoint,
        forced,
        type,
        extra,
      });
    };

    const execute = async (headers: Headers) =>
      client.request({
        document: normalizeDocument(document),
        variables,
        signal,
        requestHeaders: headers,
      });

    try {
      const result = await execute(await buildHeaders());
      return {
        data: result,
        meta: {},
      };
    } catch (error) {
      if (error instanceof ClientError) {
        const { message, request, response } = error;
        const customizedErrors = customErrors(error) as E;

        if (message?.includes('Unauthorized')) {
          const refresh_token = (getState() as AuthState).auth?.authData?.refresh_token;
          if (refresh_token) {
            const refreshResult = await store.dispatch(refreshAccessToken({ refresh_token }));
            const nextAccessToken = (refreshResult.payload as RefreshResponse | undefined)
              ?.access_token;

            if (nextAccessToken) {
              try {
                const retryResult = await execute(await buildHeaders(nextAccessToken));
                return {
                  data: retryResult,
                  meta: {},
                };
              } catch (retryError) {
                if (retryError instanceof ClientError) {
                  return {
                    error: customErrors(retryError) as E,
                    meta: { request: retryError.request, response: retryError.response },
                  };
                }
                throw retryError;
              }
            }
          }
        }

        return { error: customizedErrors, meta: { request, response } };
      }
      throw error;
    }
  };
};

export const baseQuery = graphqlRequestBaseQuery({
  url: process.env.REACT_APP_API_BASE_PATH + '/graphql',
});

function stripUndefined(obj: any) {
  if (!isPlainObject(obj)) {
    return obj;
  }
  const copy: Record<string, any> = { ...obj };
  for (const [k, v] of Object.entries(copy)) {
    if (typeof v === 'undefined') delete copy[k];
  }
  return copy;
}

export type Document = RequestDocument;
export type RequestHeaders = RequestOptions['requestHeaders'];
export type PrepareHeaders = (
  headers: Headers,
  api: Pick<BaseQueryApi, 'getState' | 'endpoint' | 'type' | 'forced' | 'extra'>,
) => MaybePromise<Headers>;

export type ErrorResponse = {
  message: string;
  stack: string;
  name: string;
};

export type GraphqlRequestBaseQueryArgs<E = ErrorResponse> = (
  | {
      url: string;
    }
  | { client: GraphQLClient }
) & {
  requestHeaders?: RequestHeaders;
  prepareHeaders?: PrepareHeaders;
  customErrors?: (args: ClientError) => E;
};

export type QueryReturnValue<T = unknown, E = unknown, M = unknown> =
  | {
      error: E;
      data?: undefined;
      meta?: M;
    }
  | {
      error?: undefined;
      data: T;
      meta?: M;
    };
export type MaybePromise<T> = T | PromiseLike<T>;
